/**
 * Pre-built screen templates for common use cases
 */

export interface ScreenTemplate {
    id: string;
    name: string;
    description: string;
    category: 'attract' | 'capture' | 'review' | 'share' | 'processing';
    icon: string;
    template: Omit<ScreenDefinition, 'id'>;
}

export const SCREEN_TEMPLATES: ScreenTemplate[] = [
    {
        id: 'attract-welcome',
        name: 'Welcome Screen',
        description: 'Simple welcome screen with start button',
        category: 'attract',
        icon: '👋',
        template: {
            displayProperties: {
                backgroundColor: '#1F2937',
                backgroundMediaUrl: '',
                foregroundColor: '#FFFFFF',
                buttonColor: '#3B82F6',
                buttonTextColor: '#FFFFFF',
                horizontalPadding: 20,
                verticalPadding: 20,
            },
            stage: {
                type: 'attract' as StageType,
                metadata: {},
            },
            elements: [
                {
                    id: 'welcome-title',
                    horizontalLocation: 'center',
                    verticalLocation: 'top',
                    order: 1,
                    type: 'text',
                    layer: 0,
                    metadata: {
                        type: 'text',
                        id: 'welcome-title',
                        text: 'Welcome to Smilebooth!',
                        fontSize: 32,
                        fontWeight: 700,
                        fontFace: 'Poppins-Bold',
                    },
                },
                {
                    id: 'welcome-subtitle',
                    horizontalLocation: 'center',
                    verticalLocation: 'center',
                    order: 2,
                    type: 'text',
                    layer: 0,
                    metadata: {
                        type: 'text',
                        id: 'welcome-subtitle',
                        text: 'Tap the button below to get started',
                        fontSize: 18,
                        fontWeight: 400,
                    },
                },
                {
                    id: 'start-button',
                    horizontalLocation: 'center',
                    verticalLocation: 'bottom',
                    order: 3,
                    type: 'button',
                    layer: 0,
                    metadata: {
                        type: 'button',
                        id: 'start-button',
                        text: 'Start Experience',
                        fontSize: 20,
                        fontWeight: 600,
                        action: 'navigate',
                        destination: 'capture-screen',
                        cornerRadius: 12,
                        horizontalPadding: 32,
                        verticalPadding: 16,
                    },
                },
            ],
            metadata: {},
            navigateDestination: undefined,
            navigateAfterTimeout: undefined,
            resetAfterTimeout: null,
        },
    },
    {
        id: 'capture-photo',
        name: 'Photo Capture',
        description: 'Photo capture screen with countdown',
        category: 'capture',
        icon: '📸',
        template: {
            displayProperties: {
                backgroundColor: '#000000',
                backgroundMediaUrl: '',
                foregroundColor: '#FFFFFF',
                buttonColor: '#EF4444',
                buttonTextColor: '#FFFFFF',
                horizontalPadding: 20,
                verticalPadding: 20,
            },
            stage: {
                type: 'capture' as StageType,
                metadata: {
                    mode: 'photo',
                    flowId: 1,
                    shouldNavigateAfterCapture: true,
                    postCaptureDestination: 'review-screen',
                    prompt: 'Get ready for your photo!',
                    countDown: 3,
                    processingMessage: 'Processing your photo...',
                    captureMessage: 'Smile!',
                    dynamicTextIdentifier: 'capture-message',
                },
            },
            elements: [
                {
                    id: 'capture-prompt',
                    horizontalLocation: 'center',
                    verticalLocation: 'top',
                    order: 1,
                    type: 'dynamicText',
                    layer: 0,
                    metadata: {
                        type: 'dynamicText',
                        id: 'capture-prompt',
                        identifier: 'capture-message',
                        fontSize: 24,
                        fontWeight: 600,
                    },
                },
                {
                    id: 'live-view',
                    horizontalLocation: 'center',
                    verticalLocation: 'center',
                    order: 2,
                    type: 'liveView',
                    layer: 0,
                    metadata: {
                        type: 'liveView',
                        id: 'live-view',
                        width: 400,
                        height: 300,
                        cornerRadius: 12,
                    },
                },
                {
                    id: 'capture-button',
                    horizontalLocation: 'center',
                    verticalLocation: 'bottom',
                    order: 3,
                    type: 'button',
                    layer: 0,
                    metadata: {
                        type: 'button',
                        id: 'capture-button',
                        text: 'Take Photo',
                        fontSize: 18,
                        fontWeight: 600,
                        action: 'navigate',
                        destination: 'review-screen',
                        cornerRadius: 12,
                        horizontalPadding: 24,
                        verticalPadding: 12,
                    },
                },
            ],
            metadata: {},
            navigateDestination: undefined,
            navigateAfterTimeout: undefined,
            resetAfterTimeout: null,
        },
    },
    {
        id: 'review-share',
        name: 'Review & Share',
        description: 'Review photo with share options',
        category: 'review',
        icon: '👀',
        template: {
            displayProperties: {
                backgroundColor: '#F9FAFB',
                backgroundMediaUrl: '',
                foregroundColor: '#1F2937',
                buttonColor: '#10B981',
                buttonTextColor: '#FFFFFF',
                horizontalPadding: 20,
                verticalPadding: 20,
            },
            stage: {
                type: 'review' as StageType,
                metadata: {},
            },
            elements: [
                {
                    id: 'review-title',
                    horizontalLocation: 'center',
                    verticalLocation: 'top',
                    order: 1,
                    type: 'text',
                    layer: 0,
                    metadata: {
                        type: 'text',
                        id: 'review-title',
                        text: 'How does this look?',
                        fontSize: 24,
                        fontWeight: 600,
                    },
                },
                {
                    id: 'photo-preview',
                    horizontalLocation: 'center',
                    verticalLocation: 'center',
                    order: 2,
                    type: 'outputPreview',
                    layer: 0,
                    metadata: {
                        type: 'outputPreview',
                        id: 'photo-preview',
                        width: 300,
                        height: 400,
                    },
                },
                {
                    id: 'share-button',
                    horizontalLocation: 'leading',
                    verticalLocation: 'bottom',
                    order: 3,
                    type: 'button',
                    layer: 0,
                    metadata: {
                        type: 'button',
                        id: 'share-button',
                        text: 'Share',
                        fontSize: 16,
                        fontWeight: 600,
                        action: 'share',
                        cornerRadius: 8,
                        horizontalPadding: 20,
                        verticalPadding: 10,
                    },
                },
                {
                    id: 'retake-button',
                    horizontalLocation: 'trailing',
                    verticalLocation: 'bottom',
                    order: 4,
                    type: 'button',
                    layer: 0,
                    metadata: {
                        type: 'button',
                        id: 'retake-button',
                        text: 'Retake',
                        fontSize: 16,
                        fontWeight: 600,
                        action: 'reset',
                        cornerRadius: 8,
                        horizontalPadding: 20,
                        verticalPadding: 10,
                    },
                },
            ],
            metadata: {},
            navigateDestination: undefined,
            navigateAfterTimeout: undefined,
            resetAfterTimeout: null,
        },
    },
    {
        id: 'share-qr',
        name: 'QR Code Share',
        description: 'Share screen with QR code',
        category: 'share',
        icon: '📱',
        template: {
            displayProperties: {
                backgroundColor: '#FFFFFF',
                backgroundMediaUrl: '',
                foregroundColor: '#1F2937',
                buttonColor: '#8B5CF6',
                buttonTextColor: '#FFFFFF',
                horizontalPadding: 20,
                verticalPadding: 20,
            },
            stage: {
                type: 'share' as StageType,
                metadata: {},
            },
            elements: [
                {
                    id: 'share-title',
                    horizontalLocation: 'center',
                    verticalLocation: 'top',
                    order: 1,
                    type: 'text',
                    layer: 0,
                    metadata: {
                        type: 'text',
                        id: 'share-title',
                        text: 'Scan to get your photo',
                        fontSize: 22,
                        fontWeight: 600,
                    },
                },
                {
                    id: 'session-qr',
                    horizontalLocation: 'center',
                    verticalLocation: 'center',
                    order: 2,
                    type: 'currentSessionQR',
                    layer: 0,
                    metadata: {
                        type: 'currentSessionQR',
                        id: 'session-qr',
                        width: 200,
                        height: 200,
                        foregroundColor: '#000000',
                        backgroundColor: '#FFFFFF',
                    },
                },
                {
                    id: 'done-button',
                    horizontalLocation: 'center',
                    verticalLocation: 'bottom',
                    order: 3,
                    type: 'button',
                    layer: 0,
                    metadata: {
                        type: 'button',
                        id: 'done-button',
                        text: 'Start Over',
                        fontSize: 16,
                        fontWeight: 600,
                        action: 'reset',
                        cornerRadius: 8,
                        horizontalPadding: 24,
                        verticalPadding: 12,
                    },
                },
            ],
            metadata: {},
            navigateDestination: undefined,
            navigateAfterTimeout: undefined,
            resetAfterTimeout: null,
        },
    },
];

export function createScreenFromTemplate(
    template: ScreenTemplate,
    customId?: string,
): ScreenDefinition {
    return {
        ...template.template,
        id:
            customId ||
            `${template.id}-${Math.random()
                .toString(36)
                .substr(2, 4)
                .toUpperCase()}`,
        elements: template.template.elements.map((element, index) => ({
            ...element,
            id: `${element.type}-${Math.random()
                .toString(36)
                .substr(2, 4)
                .toUpperCase()}`,
            metadata: {
                ...element.metadata,
                id: `${element.type}-${Math.random()
                    .toString(36)
                    .substr(2, 4)
                    .toUpperCase()}`,
            },
        })),
    };
}

export function getTemplatesByCategory(
    category?: ScreenTemplate['category'],
): ScreenTemplate[] {
    if (!category) return SCREEN_TEMPLATES;
    return SCREEN_TEMPLATES.filter(
        (template) => template.category === category,
    );
}
